import React from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import jsPDF from 'jspdf';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';

// Inline components to bypass module resolution issues
const TestComponent = () => <div className="p-8 text-center">Test Component - This works!</div>;

const Dashboard = () => (
  <div className="min-h-screen bg-gray-50 p-8">
    <div className="max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard</h1>
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Your CVs</h2>
        <p className="text-gray-600">No CVs created yet. Start by creating your first CV!</p>
        <Link 
          to="/wizard" 
          className="mt-4 inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Create New CV
        </Link>
      </div>
    </div>
  </div>
);

const Wizard = () => {
  const [currentStep, setCurrentStep] = React.useState(1);
  const [formData, setFormData] = React.useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    dateOfBirth: '',
    nationality: '',
    // Work Experience
    workExperience: [{
      company: '',
      position: '',
      startDate: '',
      endDate: '',
      current: false,
      description: ''
    }],
    // Education
    education: [{
      institution: '',
      degree: '',
      field: '',
      startDate: '',
      endDate: '',
      current: false,
      description: ''
    }],
    // Skills
    skills: {
      technical: [],
      soft: [],
      languages: []
    },
    // Languages
    languages: [{
      language: '',
      level: '',
      certificate: ''
    }],
    // Additional Information
    additionalInfo: {
      hobbies: '',
      interests: '',
      achievements: '',
      references: ''
    }
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNextStep = () => {
    if (currentStep < 9) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleDownloadPDF = () => {
    try {
      const doc = new jsPDF();
      let yPosition = 20;
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);

      // Set font
      doc.setFont('helvetica');

      // Title
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text(formData.fullName || 'Your Name', margin, yPosition);
      yPosition += 10;

      // Contact Information
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Email: ${formData.email || 'your.email@example.com'}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Phone: ${formData.phone || 'Your Phone'}`, margin, yPosition);
      yPosition += 6;
      if (formData.address) {
        doc.text(`Address: ${formData.address}`, margin, yPosition);
        yPosition += 6;
      }
      yPosition += 10;

      // Work Experience
      if (formData.workExperience.length > 0 && formData.workExperience[0].company) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('WORK EXPERIENCE', margin, yPosition);
        yPosition += 10;

        formData.workExperience.forEach((exp, index) => {
          if (exp.company) {
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(exp.position || 'Position', margin, yPosition);
            yPosition += 6;
            
            doc.setFont('helvetica', 'normal');
            doc.text(`${exp.company} • ${exp.startDate} - ${exp.current ? 'Present' : exp.endDate}`, margin, yPosition);
            yPosition += 6;
            
            if (exp.description) {
              const splitDescription = doc.splitTextToSize(exp.description, contentWidth);
              doc.text(splitDescription, margin, yPosition);
              yPosition += splitDescription.length * 4;
            }
            yPosition += 8;
          }
        });
      }

      // Education
      if (formData.education.length > 0 && formData.education[0].institution) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('EDUCATION', margin, yPosition);
        yPosition += 10;

        formData.education.forEach((edu, index) => {
          if (edu.institution) {
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(edu.degree || 'Degree', margin, yPosition);
            yPosition += 6;
            
            doc.setFont('helvetica', 'normal');
            doc.text(`${edu.institution} • ${edu.startDate} - ${edu.current ? 'Present' : edu.endDate}`, margin, yPosition);
            yPosition += 6;
            
            if (edu.field) {
              doc.text(`Field: ${edu.field}`, margin, yPosition);
              yPosition += 6;
            }
            
            if (edu.description) {
              const splitDescription = doc.splitTextToSize(edu.description, contentWidth);
              doc.text(splitDescription, margin, yPosition);
              yPosition += splitDescription.length * 4;
            }
            yPosition += 8;
          }
        });
      }

      // Skills
      if (formData.skills.technical.length > 0 || formData.skills.soft.length > 0) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('SKILLS', margin, yPosition);
        yPosition += 10;

        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        if (formData.skills.technical.length > 0) {
          doc.text(`Technical: ${formData.skills.technical.filter(s => s).join(', ')}`, margin, yPosition);
          yPosition += 6;
        }
        if (formData.skills.soft.length > 0) {
          doc.text(`Soft Skills: ${formData.skills.soft.filter(s => s).join(', ')}`, margin, yPosition);
          yPosition += 6;
        }
        yPosition += 8;
      }

      // Languages
      if (formData.languages.length > 0 && formData.languages[0].language) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('LANGUAGES', margin, yPosition);
        yPosition += 10;

        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        formData.languages.forEach((lang, index) => {
          if (lang.language) {
            doc.text(`${lang.language}: ${lang.level}${lang.certificate ? ` (${lang.certificate})` : ''}`, margin, yPosition);
            yPosition += 6;
          }
        });
      }

      // Save the PDF
      doc.save(`${formData.fullName || 'CV'}_CV.pdf`);
      
      // Show success message
      alert('✅ PDF generated successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('❌ Error generating PDF. Please try again.');
    }
  };

  const handleDownloadDOCX = async () => {
    try {
      // Create a simple HTML document that can be saved as DOCX
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>${formData.fullName || 'CV'}</title>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; margin: 40px; }
            .header { text-align: center; margin-bottom: 30px; }
            .name { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
            .contact { font-size: 14px; color: #666; }
            .section { margin-bottom: 25px; }
            .section-title { font-size: 18px; font-weight: bold; color: #333; border-bottom: 2px solid #333; padding-bottom: 5px; margin-bottom: 15px; }
            .item { margin-bottom: 15px; }
            .item-title { font-weight: bold; font-size: 16px; }
            .item-subtitle { color: #666; font-size: 14px; margin-bottom: 5px; }
            .item-description { font-size: 14px; margin-top: 5px; }
            .skills-list { font-size: 14px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="name">${formData.fullName || 'Your Name'}</div>
            <div class="contact">
              Email: ${formData.email || 'your.email@example.com'}<br>
              Phone: ${formData.phone || 'Your Phone'}<br>
              ${formData.address ? `Address: ${formData.address}` : ''}
            </div>
          </div>
      `;

      // Work Experience
      if (formData.workExperience.length > 0 && formData.workExperience[0].company) {
        htmlContent += `
          <div class="section">
            <div class="section-title">WORK EXPERIENCE</div>
        `;
        
        formData.workExperience.forEach(exp => {
          if (exp.company) {
            htmlContent += `
              <div class="item">
                <div class="item-title">${exp.position || 'Position'}</div>
                <div class="item-subtitle">${exp.company} • ${exp.startDate} - ${exp.current ? 'Present' : exp.endDate}</div>
                ${exp.description ? `<div class="item-description">${exp.description}</div>` : ''}
              </div>
            `;
          }
        });
        
        htmlContent += `</div>`;
      }

      // Education
      if (formData.education.length > 0 && formData.education[0].institution) {
        htmlContent += `
          <div class="section">
            <div class="section-title">EDUCATION</div>
        `;
        
        formData.education.forEach(edu => {
          if (edu.institution) {
            htmlContent += `
              <div class="item">
                <div class="item-title">${edu.degree || 'Degree'}</div>
                <div class="item-subtitle">${edu.institution} • ${edu.startDate} - ${edu.current ? 'Present' : edu.endDate}</div>
                ${edu.field ? `<div class="item-description">Field: ${edu.field}</div>` : ''}
                ${edu.description ? `<div class="item-description">${edu.description}</div>` : ''}
              </div>
            `;
          }
        });
        
        htmlContent += `</div>`;
      }

      // Skills
      if (formData.skills.technical.length > 0 || formData.skills.soft.length > 0) {
        htmlContent += `
          <div class="section">
            <div class="section-title">SKILLS</div>
        `;
        
        if (formData.skills.technical.length > 0) {
          htmlContent += `
            <div class="item">
              <div class="item-title">Technical Skills</div>
              <div class="skills-list">${formData.skills.technical.filter(s => s).join(', ')}</div>
            </div>
          `;
        }
        
        if (formData.skills.soft.length > 0) {
          htmlContent += `
            <div class="item">
              <div class="item-title">Soft Skills</div>
              <div class="skills-list">${formData.skills.soft.filter(s => s).join(', ')}</div>
            </div>
          `;
        }
        
        htmlContent += `</div>`;
      }

      // Languages
      if (formData.languages.length > 0 && formData.languages[0].language) {
        htmlContent += `
          <div class="section">
            <div class="section-title">LANGUAGES</div>
        `;
        
        formData.languages.forEach(lang => {
          if (lang.language) {
            htmlContent += `
              <div class="item">
                <div class="item-title">${lang.language}</div>
                <div class="item-description">Level: ${lang.level}${lang.certificate ? ` (${lang.certificate})` : ''}</div>
              </div>
            `;
          }
        });
        
        htmlContent += `</div>`;
      }

      htmlContent += `
        </body>
        </html>
      `;

      // Create and download the HTML file (which can be opened in Word)
      const blob = new Blob([htmlContent], { type: 'text/html' });
      saveAs(blob, `${formData.fullName || 'CV'}_CV.html`);
      
      // Show success message with instructions
      alert('✅ CV saved as HTML file!\n\n📝 Instructions:\n1. Open the downloaded HTML file\n2. In your browser, press Ctrl+A (select all)\n3. Press Ctrl+C (copy)\n4. Open Microsoft Word\n5. Press Ctrl+V (paste)\n6. Save as DOCX format\n\nThis creates a properly formatted Word document!');
    } catch (error) {
      console.error('DOCX generation error:', error);
      alert(`❌ Error generating DOCX: ${error.message}`);
    }
  };

  const handleSaveJSON = () => {
    // Save as JSON
    const element = document.createElement('a');
    const file = new Blob([JSON.stringify(formData, null, 2)], { type: 'application/json' });
    element.href = URL.createObjectURL(file);
    element.download = `${formData.fullName || 'CV'}_data.json`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    
    // Show success message
    alert('CV data saved as JSON file!');
  };

  const handleComplete = () => {
    // Save to localStorage for persistence
    localStorage.setItem('cvData', JSON.stringify(formData));
    
    // Show completion message
    alert('🎉 Congratulations! Your CV has been completed and saved!\n\nYou can now:\n• View it in your dashboard\n• Edit it anytime\n• Create multiple versions\n• Share with employers');
    
    // Optionally redirect to dashboard
    // window.location.href = '/dashboard';
  };

  const generateCVText = () => {
    let cvText = `CV - ${formData.fullName || 'Your Name'}\n`;
    cvText += `Email: ${formData.email || 'your.email@example.com'}\n`;
    cvText += `Phone: ${formData.phone || 'Your Phone'}\n`;
    cvText += `Address: ${formData.address || 'Your Address'}\n\n`;

    // Work Experience
    if (formData.workExperience.length > 0 && formData.workExperience[0].company) {
      cvText += `WORK EXPERIENCE\n`;
      cvText += `================\n`;
      formData.workExperience.forEach((exp, index) => {
        if (exp.company) {
          cvText += `${exp.position || 'Position'} at ${exp.company}\n`;
          cvText += `${exp.startDate} - ${exp.current ? 'Present' : exp.endDate}\n`;
          if (exp.description) {
            cvText += `${exp.description}\n`;
          }
          cvText += `\n`;
        }
      });
    }

    // Education
    if (formData.education.length > 0 && formData.education[0].institution) {
      cvText += `EDUCATION\n`;
      cvText += `==========\n`;
      formData.education.forEach((edu, index) => {
        if (edu.institution) {
          cvText += `${edu.degree || 'Degree'} from ${edu.institution}\n`;
          if (edu.field) cvText += `Field: ${edu.field}\n`;
          cvText += `${edu.startDate} - ${edu.current ? 'Present' : edu.endDate}\n`;
          if (edu.description) {
            cvText += `${edu.description}\n`;
          }
          cvText += `\n`;
        }
      });
    }

    // Skills
    if (formData.skills.technical.length > 0 || formData.skills.soft.length > 0) {
      cvText += `SKILLS\n`;
      cvText += `======\n`;
      if (formData.skills.technical.length > 0) {
        cvText += `Technical: ${formData.skills.technical.filter(s => s).join(', ')}\n`;
      }
      if (formData.skills.soft.length > 0) {
        cvText += `Soft Skills: ${formData.skills.soft.filter(s => s).join(', ')}\n`;
      }
      cvText += `\n`;
    }

    // Languages
    if (formData.languages.length > 0 && formData.languages[0].language) {
      cvText += `LANGUAGES\n`;
      cvText += `=========\n`;
      formData.languages.forEach((lang, index) => {
        if (lang.language) {
          cvText += `${lang.language}: ${lang.level}`;
          if (lang.certificate) {
            cvText += ` (${lang.certificate})`;
          }
          cvText += `\n`;
        }
      });
      cvText += `\n`;
    }

    // Additional Information
    if (formData.additionalInfo.hobbies || formData.additionalInfo.achievements) {
      cvText += `ADDITIONAL INFORMATION\n`;
      cvText += `======================\n`;
      if (formData.additionalInfo.hobbies) {
        cvText += `Hobbies & Interests: ${formData.additionalInfo.hobbies}\n`;
      }
      if (formData.additionalInfo.achievements) {
        cvText += `Achievements: ${formData.additionalInfo.achievements}\n`;
      }
    }

    return cvText;
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
              <input 
                type="text" 
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your full name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
              <input 
                type="email" 
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your email"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
              <input 
                type="tel" 
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your phone number"
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
              <textarea 
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your address"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
              <input 
                type="date" 
                value={formData.dateOfBirth}
                onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nationality</label>
              <input 
                type="text" 
                value={formData.nationality}
                onChange={(e) => handleInputChange('nationality', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your nationality"
              />
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Work Experience</h3>
              <button 
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    workExperience: [...prev.workExperience, {
                      company: '',
                      position: '',
                      startDate: '',
                      endDate: '',
                      current: false,
                      description: ''
                    }]
                  }));
                }}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
              >
                + Add Experience
              </button>
            </div>
            
            {formData.workExperience.map((exp, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-medium text-gray-900">Experience #{index + 1}</h4>
                  {formData.workExperience.length > 1 && (
                    <button 
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          workExperience: prev.workExperience.filter((_, i) => i !== index)
                        }));
                      }}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company *</label>
                    <input 
                      type="text" 
                      value={exp.company}
                      onChange={(e) => {
                        const newExp = [...formData.workExperience];
                        newExp[index].company = e.target.value;
                        setFormData(prev => ({ ...prev, workExperience: newExp }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Company name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Position *</label>
                    <input 
                      type="text" 
                      value={exp.position}
                      onChange={(e) => {
                        const newExp = [...formData.workExperience];
                        newExp[index].position = e.target.value;
                        setFormData(prev => ({ ...prev, workExperience: newExp }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Job title"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                    <input 
                      type="date" 
                      value={exp.startDate}
                      onChange={(e) => {
                        const newExp = [...formData.workExperience];
                        newExp[index].startDate = e.target.value;
                        setFormData(prev => ({ ...prev, workExperience: newExp }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input 
                      type="date" 
                      value={exp.endDate}
                      onChange={(e) => {
                        const newExp = [...formData.workExperience];
                        newExp[index].endDate = e.target.value;
                        setFormData(prev => ({ ...prev, workExperience: newExp }));
                      }}
                      disabled={exp.current}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    />
                  </div>
                </div>
                
                <div className="mt-4">
                  <label className="flex items-center">
                    <input 
                      type="checkbox" 
                      checked={exp.current}
                      onChange={(e) => {
                        const newExp = [...formData.workExperience];
                        newExp[index].current = e.target.checked;
                        if (e.target.checked) {
                          newExp[index].endDate = '';
                        }
                        setFormData(prev => ({ ...prev, workExperience: newExp }));
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">I currently work here</span>
                  </label>
                </div>
                
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Job Description</label>
                  <textarea 
                    value={exp.description}
                    onChange={(e) => {
                      const newExp = [...formData.workExperience];
                      newExp[index].description = e.target.value;
                      setFormData(prev => ({ ...prev, workExperience: newExp }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe your responsibilities and achievements"
                    rows={3}
                  />
                </div>
              </div>
            ))}
          </div>
        );
      case 4:
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Education & Qualifications</h3>
              <button 
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    education: [...prev.education, {
                      institution: '',
                      degree: '',
                      field: '',
                      startDate: '',
                      endDate: '',
                      current: false,
                      description: ''
                    }]
                  }));
                }}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
              >
                + Add Education
              </button>
            </div>
            
            {formData.education.map((edu, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-medium text-gray-900">Education #{index + 1}</h4>
                  {formData.education.length > 1 && (
                    <button 
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          education: prev.education.filter((_, i) => i !== index)
                        }));
                      }}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Institution *</label>
                    <input 
                      type="text" 
                      value={edu.institution}
                      onChange={(e) => {
                        const newEdu = [...formData.education];
                        newEdu[index].institution = e.target.value;
                        setFormData(prev => ({ ...prev, education: newEdu }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="University/School name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Degree/Qualification *</label>
                    <input 
                      type="text" 
                      value={edu.degree}
                      onChange={(e) => {
                        const newEdu = [...formData.education];
                        newEdu[index].degree = e.target.value;
                        setFormData(prev => ({ ...prev, education: newEdu }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Bachelor's, Master's, etc."
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Field of Study</label>
                    <input 
                      type="text" 
                      value={edu.field}
                      onChange={(e) => {
                        const newEdu = [...formData.education];
                        newEdu[index].field = e.target.value;
                        setFormData(prev => ({ ...prev, education: newEdu }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Computer Science, Business, etc."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                    <input 
                      type="date" 
                      value={edu.startDate}
                      onChange={(e) => {
                        const newEdu = [...formData.education];
                        newEdu[index].startDate = e.target.value;
                        setFormData(prev => ({ ...prev, education: newEdu }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input 
                      type="date" 
                      value={edu.endDate}
                      onChange={(e) => {
                        const newEdu = [...formData.education];
                        newEdu[index].endDate = e.target.value;
                        setFormData(prev => ({ ...prev, education: newEdu }));
                      }}
                      disabled={edu.current}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    />
                  </div>
                </div>
                
                <div className="mt-4">
                  <label className="flex items-center">
                    <input 
                      type="checkbox" 
                      checked={edu.current}
                      onChange={(e) => {
                        const newEdu = [...formData.education];
                        newEdu[index].current = e.target.checked;
                        if (e.target.checked) {
                          newEdu[index].endDate = '';
                        }
                        setFormData(prev => ({ ...prev, education: newEdu }));
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Currently studying</span>
                  </label>
                </div>
                
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea 
                    value={edu.description}
                    onChange={(e) => {
                      const newEdu = [...formData.education];
                      newEdu[index].description = e.target.value;
                      setFormData(prev => ({ ...prev, education: newEdu }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Relevant coursework, achievements, GPA, etc."
                    rows={3}
                  />
                </div>
              </div>
            ))}
          </div>
        );
      case 5:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Skills & Competencies</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Technical Skills</label>
                <div className="space-y-2">
                  {formData.skills.technical.map((skill, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input 
                        type="text" 
                        value={skill}
                        onChange={(e) => {
                          const newSkills = [...formData.skills.technical];
                          newSkills[index] = e.target.value;
                          setFormData(prev => ({ 
                            ...prev, 
                            skills: { ...prev.skills, technical: newSkills }
                          }));
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="JavaScript, Python, React, etc."
                      />
                      <button 
                        type="button"
                        onClick={() => {
                          const newSkills = formData.skills.technical.filter((_, i) => i !== index);
                          setFormData(prev => ({ 
                            ...prev, 
                            skills: { ...prev.skills, technical: newSkills }
                          }));
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button 
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ 
                        ...prev, 
                        skills: { 
                          ...prev.skills, 
                          technical: [...prev.skills.technical, ''] 
                        }
                      }));
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    + Add Technical Skill
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Soft Skills</label>
                <div className="space-y-2">
                  {formData.skills.soft.map((skill, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input 
                        type="text" 
                        value={skill}
                        onChange={(e) => {
                          const newSkills = [...formData.skills.soft];
                          newSkills[index] = e.target.value;
                          setFormData(prev => ({ 
                            ...prev, 
                            skills: { ...prev.skills, soft: newSkills }
                          }));
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Leadership, Communication, etc."
                      />
                      <button 
                        type="button"
                        onClick={() => {
                          const newSkills = formData.skills.soft.filter((_, i) => i !== index);
                          setFormData(prev => ({ 
                            ...prev, 
                            skills: { ...prev.skills, soft: newSkills }
                          }));
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button 
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ 
                        ...prev, 
                        skills: { 
                          ...prev.skills, 
                          soft: [...prev.skills.soft, ''] 
                        }
                      }));
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    + Add Soft Skill
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      case 6:
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Languages</h3>
              <button 
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    languages: [...prev.languages, {
                      language: '',
                      level: '',
                      certificate: ''
                    }]
                  }));
                }}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
              >
                + Add Language
              </button>
            </div>
            
            {formData.languages.map((lang, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-medium text-gray-900">Language #{index + 1}</h4>
                  {formData.languages.length > 1 && (
                    <button 
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          languages: prev.languages.filter((_, i) => i !== index)
                        }));
                      }}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Language *</label>
                    <input 
                      type="text" 
                      value={lang.language}
                      onChange={(e) => {
                        const newLangs = [...formData.languages];
                        newLangs[index].language = e.target.value;
                        setFormData(prev => ({ ...prev, languages: newLangs }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="English, Spanish, French, etc."
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Proficiency Level *</label>
                    <select 
                      value={lang.level}
                      onChange={(e) => {
                        const newLangs = [...formData.languages];
                        newLangs[index].level = e.target.value;
                        setFormData(prev => ({ ...prev, languages: newLangs }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select level</option>
                      <option value="A1">A1 - Beginner</option>
                      <option value="A2">A2 - Elementary</option>
                      <option value="B1">B1 - Intermediate</option>
                      <option value="B2">B2 - Upper Intermediate</option>
                      <option value="C1">C1 - Advanced</option>
                      <option value="C2">C2 - Proficient</option>
                      <option value="Native">Native</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Certificate</label>
                    <input 
                      type="text" 
                      value={lang.certificate}
                      onChange={(e) => {
                        const newLangs = [...formData.languages];
                        newLangs[index].certificate = e.target.value;
                        setFormData(prev => ({ ...prev, languages: newLangs }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="TOEFL, IELTS, DELE, etc."
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      case 7:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hobbies & Interests</label>
                <textarea 
                  value={formData.additionalInfo.hobbies}
                  onChange={(e) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      additionalInfo: { ...prev.additionalInfo, hobbies: e.target.value }
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Sports, music, reading, volunteering, etc."
                  rows={3}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Achievements & Awards</label>
                <textarea 
                  value={formData.additionalInfo.achievements}
                  onChange={(e) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      additionalInfo: { ...prev.additionalInfo, achievements: e.target.value }
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Awards, certifications, publications, etc."
                  rows={3}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">References</label>
                <textarea 
                  value={formData.additionalInfo.references}
                  onChange={(e) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      additionalInfo: { ...prev.additionalInfo, references: e.target.value }
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Professional references (optional)"
                  rows={3}
                />
              </div>
            </div>
          </div>
        );
      case 8:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">CV Preview & Review</h3>
            
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{formData.fullName || 'Your Name'}</h2>
                <p className="text-gray-600">{formData.email || 'your.email@example.com'}</p>
                <p className="text-gray-600">{formData.phone || 'Your Phone'}</p>
              </div>
              
              {formData.workExperience.length > 0 && formData.workExperience[0].company && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-1">Work Experience</h3>
                  {formData.workExperience.map((exp, index) => (
                    <div key={index} className="mb-4">
                      <h4 className="font-semibold text-gray-900">{exp.position}</h4>
                      <p className="text-gray-600">{exp.company} • {exp.startDate} - {exp.current ? 'Present' : exp.endDate}</p>
                      {exp.description && <p className="text-gray-700 mt-2">{exp.description}</p>}
                    </div>
                  ))}
                </div>
              )}
              
              {formData.education.length > 0 && formData.education[0].institution && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-1">Education</h3>
                  {formData.education.map((edu, index) => (
                    <div key={index} className="mb-4">
                      <h4 className="font-semibold text-gray-900">{edu.degree}</h4>
                      <p className="text-gray-600">{edu.institution} • {edu.startDate} - {edu.current ? 'Present' : edu.endDate}</p>
                      {edu.field && <p className="text-gray-700">{edu.field}</p>}
                      {edu.description && <p className="text-gray-700 mt-2">{edu.description}</p>}
                    </div>
                  ))}
                </div>
              )}
              
              {formData.skills.technical.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-1">Skills</h3>
                  <p className="text-gray-700">
                    <strong>Technical:</strong> {formData.skills.technical.filter(s => s).join(', ')}
                  </p>
                  {formData.skills.soft.length > 0 && (
                    <p className="text-gray-700 mt-2">
                      <strong>Soft Skills:</strong> {formData.skills.soft.filter(s => s).join(', ')}
                    </p>
                  )}
                </div>
              )}
              
              {formData.languages.length > 0 && formData.languages[0].language && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-1">Languages</h3>
                  {formData.languages.map((lang, index) => (
                    <div key={index} className="mb-2">
                      <p className="text-gray-700">
                        <strong>{lang.language}:</strong> {lang.level}
                        {lang.certificate && ` (${lang.certificate})`}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Ready to create your CV?</h4>
              <p className="text-blue-800 text-sm">
                Review your information above and click "Next Step" to proceed to the final step where you can export your CV.
              </p>
            </div>
          </div>
        );
      case 9:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Export & Download</h3>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-4">🎉 Congratulations! Your CV is ready!</h4>
              <p className="text-green-800 mb-6">
                You've successfully completed all steps of the CV wizard. Choose your preferred format to download your CV.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button 
                  onClick={handleDownloadPDF}
                  className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
                >
                  📄 Download PDF
                </button>
                <button 
                  onClick={handleDownloadDOCX}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  📝 Download DOCX
                </button>
                <button 
                  onClick={handleSaveJSON}
                  className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  💾 Save as JSON
                </button>
              </div>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">What's next?</h4>
              <ul className="text-gray-700 text-sm space-y-1">
                <li>• Your CV has been saved to your dashboard</li>
                <li>• You can edit it anytime from the dashboard</li>
                <li>• Create multiple versions for different job applications</li>
                <li>• Share your CV with potential employers</li>
              </ul>
            </div>
          </div>
        );
      default:
        return (
          <div className="space-y-4">
            <div className="text-center py-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Step {currentStep}</h3>
              <p className="text-gray-600">This step is coming soon!</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">CV Wizard</h1>
        
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Step {currentStep} of 9</span>
            <span className="text-sm text-gray-500">{Math.round((currentStep / 9) * 100)}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / 9) * 100}%` }}
            ></div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">
            {currentStep === 1 && "Step 1: Personal Information"}
            {currentStep === 2 && "Step 2: Contact Details"}
            {currentStep === 3 && "Step 3: Work Experience"}
            {currentStep === 4 && "Step 4: Education & Qualifications"}
            {currentStep === 5 && "Step 5: Skills & Competencies"}
            {currentStep === 6 && "Step 6: Languages"}
            {currentStep === 7 && "Step 7: Additional Information"}
            {currentStep === 8 && "Step 8: CV Preview & Review"}
            {currentStep === 9 && "Step 9: Export & Download"}
          </h2>
          <p className="text-gray-600 mb-6">
            {currentStep === 1 && "Let's start by gathering your basic information."}
            {currentStep === 2 && "Please provide your contact details and personal information."}
            {currentStep === 3 && "Add your work experience to showcase your professional background."}
            {currentStep === 4 && "List your educational background and qualifications."}
            {currentStep === 5 && "Highlight your technical and soft skills."}
            {currentStep === 6 && "Add languages you speak and your proficiency levels."}
            {currentStep === 7 && "Include any additional information that might be relevant."}
            {currentStep === 8 && "Review your CV before finalizing it."}
            {currentStep === 9 && "Download your completed CV in your preferred format."}
          </p>
          
          {renderStep()}
          
          <div className="flex justify-between mt-8">
            <button 
              onClick={handlePrevStep}
              disabled={currentStep === 1}
              className={`px-6 py-2 rounded-lg transition-colors ${
                currentStep === 1 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-gray-600 text-white hover:bg-gray-700'
              }`}
            >
              Previous
            </button>
            
            <button 
              onClick={currentStep === 9 ? handleComplete : handleNextStep}
              className={`px-6 py-2 rounded-lg transition-colors ${
                currentStep === 9 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {currentStep === 9 ? 'Complete' : 'Next Step'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Preview = () => (
  <div className="min-h-screen bg-gray-50 p-8">
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">CV Preview</h1>
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Your CV Preview</h2>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <p className="text-gray-500">CV preview will appear here once you complete the wizard.</p>
          <Link 
            to="/wizard" 
            className="mt-4 inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Start Creating CV
          </Link>
        </div>
      </div>
    </div>
  </div>
);

function App() {
  return (
    <div className="App">
      <Helmet>
        <title>Europass CV Builder</title>
        <meta name="description" content="Create professional Europass-compliant CVs with our guided 9-step wizard" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>
      
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-xl font-bold text-gray-900">
              Europass CV Builder
            </Link>
            <div className="flex space-x-4">
              <Link to="/dashboard" className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium">
                Dashboard
              </Link>
              <Link to="/wizard" className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium">
                Create CV
              </Link>
              <Link to="/preview" className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium">
                Preview
              </Link>
            </div>
          </div>
        </div>
      </nav>
      
      <Routes>
        <Route path="/" element={
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="max-w-4xl mx-auto text-center px-4">
              <h1 className="text-4xl font-bold text-gray-900 mb-6">Europass CV Builder</h1>
              <p className="text-xl text-gray-600 mb-8">Create professional Europass-compliant CVs with our guided 9-step wizard</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/wizard" 
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Start Creating CV
                </Link>
                <Link 
                  to="/dashboard" 
                  className="bg-gray-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors"
                >
                  View Dashboard
                </Link>
              </div>
            </div>
          </div>
        } />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/wizard" element={<Wizard />} />
        <Route path="/preview" element={<Preview />} />
        <Route path="*" element={
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Page Not Found</h1>
              <p className="text-gray-600 mb-8">The page you're looking for doesn't exist.</p>
              <Link 
                to="/" 
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Go Home
              </Link>
            </div>
          </div>
        } />
      </Routes>
    </div>
  );
}

export default App;
